import type { PaymentProviderType } from './types';

export const PAYMENT_DOCUMENT_TYPES = {
  TaxInvoiceAndReceipt: 1,
  DonationReceipt: 405,
  Receipt: 3
} as const;

export type PaymentDocumentTypeValue = (typeof PAYMENT_DOCUMENT_TYPES)[keyof typeof PAYMENT_DOCUMENT_TYPES];

/**
 * Initial supportedDocTypes for a new provider — Grow defaults to donation receipt only.
 * Jaffa too, unless a separate service page code (non-donation receipt) is configured.
 */
export function defaultSupportedDocTypesForProvider(
  providerType: PaymentProviderType,
  credentials?: { jaffaServicePageGuid?: unknown } | null
): number[] {
  if (providerType === 'grow') return [PAYMENT_DOCUMENT_TYPES.DonationReceipt];
  if (providerType === 'jaffa' && !String(credentials?.jaffaServicePageGuid ?? '').trim()) {
    return [PAYMENT_DOCUMENT_TYPES.DonationReceipt];
  }
  return [];
}

/**
 * Empty/null supportedDocTypes = all document types allowed.
 * Non-empty = only listed types.
 */
export function isPaymentProviderCapableForDocumentType(
  supportedDocTypes: number[] | null | undefined,
  documentType?: number | null
): boolean {
  if (documentType == null) return true;
  if (!Array.isArray(supportedDocTypes) || supportedDocTypes.length === 0) return true;
  return supportedDocTypes.includes(documentType);
}

export type PersonalProviderPickCandidate = {
  id: number;
  providerType: PaymentProviderType;
  forDocTypes: number[];
  supportedDocTypes: number[];
  isDefault: boolean;
  isActive: boolean;
  isArchived?: boolean;
};

export type PickPersonalProviderOptions = {
  personalPaymentProviderId?: number | null;
  documentType?: number | null;
  preferProviderType?: PaymentProviderType;
  allowInactive?: boolean;
};

function isCapablePersonalProvider(provider: PersonalProviderPickCandidate, documentType?: number | null): boolean {
  return (
    !provider.isArchived &&
    provider.isActive !== false &&
    isPaymentProviderCapableForDocumentType(provider.supportedDocTypes, documentType)
  );
}

function pickAmongCapable<T extends PersonalProviderPickCandidate>(
  capable: T[],
  documentType?: number | null
): T | null {
  if (capable.length === 0) return null;

  if (documentType != null) {
    const byDocType = capable.find(p => p.forDocTypes.includes(documentType));
    if (byDocType) return byDocType;
  }

  return capable.find(p => p.isDefault) ?? capable[0] ?? null;
}

/** Mirrors backend resolvePaymentProvider personal-provider selection. */
export function pickPersonalProvider<T extends PersonalProviderPickCandidate>(
  providers: T[],
  opts: PickPersonalProviderOptions = {}
): T | null {
  if (opts.personalPaymentProviderId != null) {
    const pinned = providers.find(p => p.id === opts.personalPaymentProviderId);
    if (pinned) {
      if (opts.allowInactive) {
        if (isPaymentProviderCapableForDocumentType(pinned.supportedDocTypes, opts.documentType)) {
          return pinned;
        }
      } else if (isCapablePersonalProvider(pinned, opts.documentType)) {
        return pinned;
      }
    }
  }

  let capable = providers.filter((p): p is T => isCapablePersonalProvider(p, opts.documentType));
  if (opts.preferProviderType) {
    capable = capable.filter(p => p.providerType === opts.preferProviderType);
  }

  return pickAmongCapable(capable, opts.documentType);
}
